import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  User, Building2, Phone, Mail, MapPin, Users, FileText, 
  Home, Search, Plus, Trash2, Loader2, Calendar, UserPlus
} from "lucide-react";
import { API } from "@/App";
import { PostcodeLookup } from "@/components/PostcodeLookup";

// Constants for dropdowns
const TITLES = ["Mr", "Mrs", "Miss", "Ms", "Dr", "Prof", "Rev", "Sir", "Lady"];
const GENDERS = ["Male", "Female", "Prefer not to say"];
const STATES = ["Looking", "Registered", "Under Offer", "Purchased", "Withdrawn"];
const SOURCES = ["Rightmove", "Zoopla", "OnTheMarket", "Website", "Walk-in", "Referral", "Returning Client", "Social Media", "Other"];
const FINANCE_STATUSES = ["Not Started", "AIP Obtained", "Full Application Submitted", "Mortgage Offered", "Cash Buyer"];
const CHAIN_STATUSES = ["No Chain", "First Time Buyer", "House to Sell", "House Under Offer", "House SSTC", "Renting"];
const BRANCHES = ["Bedford Office", "Cranfield Office", "Kempston Office"];

const PROPERTY_TYPES = [
  "Apartment", "Barn Conversion", "Bungalow", "Chalet", "Chalet Bungalow",
  "Coach House", "Cottage", "Detached Bungalow", "Detached House", "Double Room",
  "End-Terraced House", "Flat", "Garage", "House", "House Boat",
  "Land", "Link Detached House", "Maisonette", "Park Home", "Penthouse",
  "Retirement Property", "Semi-Detached House", "Single Room", "Studio",
  "Terraced House", "Town House"
];

const AREAS = [
  "Abbeyfields", "Bedford", "Biddenham", "Brickhill", "Bromham", "Carlton",
  "Castle Road area", "Clapham", "Cranfield", "Elstow", "Flitwick",
  "Great Denham", "Harrold", "Houghton Conquest", "Kempston", "Lidlington",
  "Lower Shelton", "Marston Moretaine", "Milton Keynes", "New Cardington",
  "Oakley", "Putnoe", "Queens Park", "Riverfield Drive", "Sharnbrook",
  "Shortstown", "South Bedford", "Stewartby", "Wilstead", "Wixams", "Wootton"
];

const PRICE_OPTIONS = [
  { value: "any", label: "No minimum" },
  { value: "50000", label: "£50,000" },
  { value: "75000", label: "£75,000" },
  { value: "100000", label: "£100,000" },
  { value: "125000", label: "£125,000" },
  { value: "150000", label: "£150,000" },
  { value: "175000", label: "£175,000" },
  { value: "200000", label: "£200,000" },
  { value: "225000", label: "£225,000" },
  { value: "250000", label: "£250,000" },
  { value: "275000", label: "£275,000" },
  { value: "300000", label: "£300,000" },
  { value: "325000", label: "£325,000" },
  { value: "350000", label: "£350,000" },
  { value: "400000", label: "£400,000" },
  { value: "450000", label: "£450,000" },
  { value: "500000", label: "£500,000" },
  { value: "550000", label: "£550,000" },
  { value: "600000", label: "£600,000" },
  { value: "700000", label: "£700,000" },
  { value: "800000", label: "£800,000" },
  { value: "900000", label: "£900,000" },
  { value: "1000000", label: "£1,000,000" },
  { value: "1500000", label: "£1,500,000" },
  { value: "2000000", label: "£2,000,000+" }
];

const BEDROOM_OPTIONS = [
  { value: "studio", label: "Studio" },
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5" },
  { value: "6", label: "6+" },
  { value: "99", label: "Maximum" }
];

export const AddApplicantForm = ({ open, onClose, onOpenChange, onSuccess, staffList = [], initialData = null }) => {
  const token = localStorage.getItem("token");
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  
  // Default form state
  const getDefaultFormData = () => ({
    // Basic Details
    is_business: false,
    business_name: "",
    contacts: [{
      title: "",
      first_name: "",
      last_name: "",
      friendly_name: "",
      gender: "",
      mobile_1: "",
      mobile_2: "",
      home_tel: "",
      work_tel: "",
      email_1: "",
      email_2: ""
    }],
    
    // Address
    address: {
      line_1: "",
      line_2: "",
      town: "",
      county: "",
      postcode: "",
      country: "United Kingdom"
    },
    
    // Status & Management
    state: "Looking",
    lead_staff: "",
    registration_complete: false,
    source: "",
    grouping: "",
    do_not_delete_before: "",
    notes: "",
    
    // Finance & Chain
    finance_status: "",
    finance_status_notes: "",
    chain_status: "",
    chain_status_notes: "",
    
    // Notifications
    exclude_viewing_email: false,
    exclude_viewing_sms: false,
    exclude_appointment_email: false,
    exclude_appointment_sms: false,
    
    // Branches & Categories
    branches: ["Bedford Office", "Cranfield Office", "Kempston Office"],
    property_categories: ["residential"],
    
    // Search Criteria
    valid_until: "",
    price_min: "",
    price_max: "",
    bedrooms_min: "",
    bedrooms_max: "",
    property_types: [],
    areas: [],
    additional_notes: "",
    
    // Pre-filled property reference
    interested_property: null
  });

  // Form state - merge initial data if provided
  const [formData, setFormData] = useState(() => {
    if (initialData) {
      const defaults = getDefaultFormData();
      return {
        ...defaults,
        ...initialData,
        contacts: initialData.contacts || defaults.contacts,
        address: { ...defaults.address, ...(initialData.address || {}) }
      };
    }
    return getDefaultFormData();
  });
  
  // Reset form when modal opens with new initial data
  useEffect(() => {
    if (open) {
      if (initialData) {
        const defaults = getDefaultFormData();
        setFormData({
          ...defaults,
          ...initialData,
          contacts: initialData.contacts || defaults.contacts,
          address: { ...defaults.address, ...(initialData.address || {}) }
        });
      } else {
        setFormData(getDefaultFormData());
      }
      setActiveTab("basic");
    }
  }, [open, initialData]);
  
  // Handle close - support both onClose and onOpenChange
  const handleClose = () => {
    if (onOpenChange) {
      onOpenChange(false);
    } else if (onClose) {
      onClose();
    }
  };

  const updateContact = (index, field, value) => {
    const newContacts = [...formData.contacts];
    newContacts[index] = { ...newContacts[index], [field]: value };
    setFormData({ ...formData, contacts: newContacts });
  };

  const addContact = () => {
    setFormData({
      ...formData,
      contacts: [...formData.contacts, {
        title: "",
        first_name: "",
        last_name: "",
        friendly_name: "",
        gender: "",
        mobile_1: "",
        mobile_2: "",
        home_tel: "",
        work_tel: "",
        email_1: "",
        email_2: ""
      }]
    });
  };

  const removeContact = (index) => {
    if (formData.contacts.length <= 1) return;
    const newContacts = formData.contacts.filter((_, i) => i !== index);
    setFormData({ ...formData, contacts: newContacts });
  };

  const toggleArrayItem = (field, item) => {
    const current = formData[field] || [];
    const updated = current.includes(item)
      ? current.filter(i => i !== item)
      : [...current, item];
    setFormData({ ...formData, [field]: updated });
  };

  const handleSubmit = async () => {
    // Validation
    const firstContact = formData.contacts[0];
    if (!firstContact.first_name || !firstContact.last_name) {
      toast.error("First name and last name are required");
      setActiveTab("basic");
      return;
    }

    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Helper to parse price/bedroom values
      const parseNum = (val) => {
        if (!val || val === "any" || val === "studio") return null;
        return parseInt(val);
      };
      
      // Prepare payload
      const payload = {
        is_business: formData.is_business,
        business_name: formData.business_name || null,
        contacts: formData.contacts,
        address: formData.address.line_1 ? formData.address : null,
        
        state: formData.state.toLowerCase(),
        lead_staff: formData.lead_staff || null,
        registration_complete: formData.registration_complete,
        source: formData.source || null,
        grouping: formData.grouping || null,
        do_not_delete_before: formData.do_not_delete_before || null,
        notes: formData.notes || null,
        
        finance_status: formData.finance_status || null,
        finance_status_notes: formData.finance_status_notes || null,
        chain_status: formData.chain_status || null,
        chain_status_notes: formData.chain_status_notes || null,
        
        exclude_viewing_email: formData.exclude_viewing_email,
        exclude_viewing_sms: formData.exclude_viewing_sms,
        exclude_appointment_email: formData.exclude_appointment_email,
        exclude_appointment_sms: formData.exclude_appointment_sms,
        
        branches: formData.branches,
        property_categories: formData.property_categories,
        
        valid_until: formData.valid_until || null,
        price_min: parseNum(formData.price_min),
        price_max: parseNum(formData.price_max),
        bedrooms_min: parseNum(formData.bedrooms_min),
        bedrooms_max: parseNum(formData.bedrooms_max),
        property_types: formData.property_types,
        areas: formData.areas,
        additional_notes: formData.additional_notes || null
      };

      await axios.post(`${API}/applicants`, payload, { headers });
      
      toast.success("Applicant registered successfully");
      onSuccess?.();
      handleClose();
      
      // Reset form
      setFormData(getDefaultFormData());
      setActiveTab("basic");
      
    } catch (error) {
      console.error("Error creating applicant:", error);
      toast.error(error.response?.data?.detail || "Failed to create applicant");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <UserPlus className="w-5 h-5" />
            Register New Buyer Applicant
            {formData.interested_property && (
              <span className="text-sm font-normal text-slate-500 ml-2">
                (Interested in: {formData.interested_property.address})
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="px-6">
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="basic" className="text-xs sm:text-sm">
                <User className="w-4 h-4 mr-1 hidden sm:block" />
                Basic Details
              </TabsTrigger>
              <TabsTrigger value="status" className="text-xs sm:text-sm">
                <FileText className="w-4 h-4 mr-1 hidden sm:block" />
                Status
              </TabsTrigger>
              <TabsTrigger value="finance" className="text-xs sm:text-sm">
                <Building2 className="w-4 h-4 mr-1 hidden sm:block" />
                Finance & Chain
              </TabsTrigger>
              <TabsTrigger value="search" className="text-xs sm:text-sm">
                <Search className="w-4 h-4 mr-1 hidden sm:block" />
                Search Criteria
              </TabsTrigger>
            </TabsList>
          </div>
          
          <ScrollArea className="h-[60vh] px-6 py-4">
            {/* TAB 1: Basic Details */}
            <TabsContent value="basic" className="mt-0 space-y-6">
              {/* Business toggle */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_business"
                  checked={formData.is_business}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_business: checked })}
                />
                <Label htmlFor="is_business">This is a business/company</Label>
              </div>
              
              {formData.is_business && (
                <div>
                  <Label>Company Name</Label>
                  <Input
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    placeholder="Company name"
                  />
                </div>
              )}
              
              {/* Contacts */}
              {formData.contacts.map((contact, idx) => (
                <Card key={idx} className="border-slate-200">
                  <CardHeader className="py-3 px-4 bg-slate-50 flex flex-row items-center justify-between">
                    <CardTitle className="text-sm font-medium">
                      {idx === 0 ? "Primary Contact" : `Additional Contact ${idx}`}
                    </CardTitle>
                    {idx > 0 && (
                      <Button variant="ghost" size="sm" onClick={() => removeContact(idx)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent className="p-4 space-y-4">
                    {/* Name row */}
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs">Title</Label>
                        <Select value={contact.title} onValueChange={(v) => updateContact(idx, "title", v)}>
                          <SelectTrigger><SelectValue placeholder="Title" /></SelectTrigger>
                          <SelectContent>
                            {TITLES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">First Name *</Label>
                        <Input
                          value={contact.first_name}
                          onChange={(e) => updateContact(idx, "first_name", e.target.value)}
                          placeholder="First name"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Last Name *</Label>
                        <Input
                          value={contact.last_name}
                          onChange={(e) => updateContact(idx, "last_name", e.target.value)}
                          placeholder="Last name"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Friendly Name</Label>
                        <Input
                          value={contact.friendly_name}
                          onChange={(e) => updateContact(idx, "friendly_name", e.target.value)}
                          placeholder="e.g. Jack"
                        />
                        <p className="text-[10px] text-slate-500 mt-1">Used in salutation</p>
                      </div>
                    </div>
                    
                    {/* Gender */}
                    <div className="w-1/4">
                      <Label className="text-xs">Gender</Label>
                      <Select value={contact.gender} onValueChange={(v) => updateContact(idx, "gender", v)}>
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {GENDERS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-slate-500 mt-1">For Dear Sir/Madam</p>
                    </div>
                    
                    {/* Phones */}
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs">Mobile Phone</Label>
                        <Input
                          value={contact.mobile_1}
                          onChange={(e) => updateContact(idx, "mobile_1", e.target.value)}
                          placeholder="07..."
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Mobile 2</Label>
                        <Input
                          value={contact.mobile_2}
                          onChange={(e) => updateContact(idx, "mobile_2", e.target.value)}
                          placeholder="Secondary mobile"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Home Phone</Label>
                        <Input
                          value={contact.home_tel}
                          onChange={(e) => updateContact(idx, "home_tel", e.target.value)}
                          placeholder="Home"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Work Phone</Label>
                        <Input
                          value={contact.work_tel}
                          onChange={(e) => updateContact(idx, "work_tel", e.target.value)}
                          placeholder="Work"
                        />
                      </div>
                    </div>
                    
                    {/* Emails */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Email</Label>
                        <Input
                          type="email"
                          value={contact.email_1}
                          onChange={(e) => updateContact(idx, "email_1", e.target.value)}
                          placeholder="email@example.com"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Secondary Email</Label>
                        <Input
                          type="email"
                          value={contact.email_2}
                          onChange={(e) => updateContact(idx, "email_2", e.target.value)}
                          placeholder="Secondary email"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              <Button variant="outline" size="sm" onClick={addContact} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Add Additional Contact
              </Button>
              
              {/* Address */}
              <Card>
                <CardHeader className="py-3 px-4 bg-slate-50">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Current Address
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  {/* Postcode Lookup */}
                  <PostcodeLookup
                    onAddressSelect={(addr) => {
                      setFormData({
                        ...formData,
                        address: {
                          line_1: addr.line_1,
                          line_2: addr.line_2 || "",
                          town: addr.town,
                          county: addr.county || "",
                          postcode: addr.postcode,
                          country: "United Kingdom"
                        }
                      });
                    }}
                  />
                  
                  <div className="border-t pt-3">
                    <p className="text-xs text-slate-500 mb-2">Or enter manually:</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Address Line 1</Label>
                      <Input
                        value={formData.address.line_1}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          address: { ...formData.address, line_1: e.target.value }
                        })}
                        placeholder="House number and street"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Address Line 2</Label>
                      <Input
                        value={formData.address.line_2}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          address: { ...formData.address, line_2: e.target.value }
                        })}
                        placeholder="Apartment, suite, etc."
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Town/City</Label>
                      <Input
                        value={formData.address.town}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          address: { ...formData.address, town: e.target.value }
                        })}
                        placeholder="Town or city"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">County</Label>
                      <Input
                        value={formData.address.county}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          address: { ...formData.address, county: e.target.value }
                        })}
                        placeholder="County"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Postcode</Label>
                      <Input
                        value={formData.address.postcode}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          address: { ...formData.address, postcode: e.target.value }
                        })}
                        placeholder="MK42 xxx"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* TAB 2: Status & Management */}
            <TabsContent value="status" className="mt-0 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>State *</Label>
                  <Select value={formData.state} onValueChange={(v) => setFormData({ ...formData, state: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Lead Staff</Label>
                  <Select value={formData.lead_staff} onValueChange={(v) => setFormData({ ...formData, lead_staff: v })}>
                    <SelectTrigger><SelectValue placeholder="Assign staff member" /></SelectTrigger>
                    <SelectContent>
                      {staffList.map(s => (
                        <SelectItem key={s.user_id || s.name} value={s.name}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Source</Label>
                  <Select value={formData.source} onValueChange={(v) => setFormData({ ...formData, source: v })}>
                    <SelectTrigger><SelectValue placeholder="How did they find us?" /></SelectTrigger>
                    <SelectContent>
                      {SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Grouping</Label>
                  <Input
                    value={formData.grouping}
                    onChange={(e) => setFormData({ ...formData, grouping: e.target.value })}
                    placeholder="Optional grouping"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Do Not Delete Before</Label>
                  <Input
                    type="date"
                    value={formData.do_not_delete_before}
                    onChange={(e) => setFormData({ ...formData, do_not_delete_before: e.target.value })}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Keep contact data beyond standard retention policy
                  </p>
                </div>
                <div className="flex items-center space-x-2 pt-6">
                  <Checkbox
                    id="registration_complete"
                    checked={formData.registration_complete}
                    onCheckedChange={(checked) => setFormData({ ...formData, registration_complete: checked })}
                  />
                  <Label htmlFor="registration_complete">Registration Complete</Label>
                </div>
              </div>
              
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="General notes about the applicant..."
                  rows={4}
                />
              </div>
              
              {/* Notification Preferences */}
              <Card>
                <CardHeader className="py-3 px-4 bg-slate-50">
                  <CardTitle className="text-sm font-medium">Notification Preferences</CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium mb-2">Exclude from viewing reminders:</p>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={formData.exclude_viewing_email}
                            onCheckedChange={(c) => setFormData({ ...formData, exclude_viewing_email: c })}
                          />
                          Email
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={formData.exclude_viewing_sms}
                            onCheckedChange={(c) => setFormData({ ...formData, exclude_viewing_sms: c })}
                          />
                          SMS
                        </label>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-2">Exclude from appointment reminders:</p>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={formData.exclude_appointment_email}
                            onCheckedChange={(c) => setFormData({ ...formData, exclude_appointment_email: c })}
                          />
                          Email
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={formData.exclude_appointment_sms}
                            onCheckedChange={(c) => setFormData({ ...formData, exclude_appointment_sms: c })}
                          />
                          SMS
                        </label>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Branches */}
              <Card>
                <CardHeader className="py-3 px-4 bg-slate-50">
                  <CardTitle className="text-sm font-medium">Branches</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-4">
                    {BRANCHES.map(branch => (
                      <label key={branch} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={formData.branches.includes(branch)}
                          onCheckedChange={() => toggleArrayItem("branches", branch)}
                        />
                        {branch}
                      </label>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              {/* Property Categories */}
              <Card>
                <CardHeader className="py-3 px-4 bg-slate-50">
                  <CardTitle className="text-sm font-medium">Property Categories</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={formData.property_categories.includes("residential")}
                        onCheckedChange={() => toggleArrayItem("property_categories", "residential")}
                      />
                      Residential
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={formData.property_categories.includes("commercial")}
                        onCheckedChange={() => toggleArrayItem("property_categories", "commercial")}
                      />
                      Commercial
                    </label>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* TAB 3: Finance & Chain */}
            <TabsContent value="finance" className="mt-0 space-y-6">
              <Card>
                <CardHeader className="py-3 px-4 bg-slate-50">
                  <CardTitle className="text-sm font-medium">Finance Status</CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <Select value={formData.finance_status} onValueChange={(v) => setFormData({ ...formData, finance_status: v })}>
                    <SelectTrigger><SelectValue placeholder="Select finance status" /></SelectTrigger>
                    <SelectContent>
                      {FINANCE_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div>
                    <Label className="text-xs">Finance Status Notes</Label>
                    <Textarea
                      value={formData.finance_status_notes}
                      onChange={(e) => setFormData({ ...formData, finance_status_notes: e.target.value })}
                      placeholder="Details about their finance situation..."
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="py-3 px-4 bg-slate-50">
                  <CardTitle className="text-sm font-medium">Chain Status</CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <Select value={formData.chain_status} onValueChange={(v) => setFormData({ ...formData, chain_status: v })}>
                    <SelectTrigger><SelectValue placeholder="Select chain status" /></SelectTrigger>
                    <SelectContent>
                      {CHAIN_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div>
                    <Label className="text-xs">Chain Status Notes</Label>
                    <Textarea
                      value={formData.chain_status_notes}
                      onChange={(e) => setFormData({ ...formData, chain_status_notes: e.target.value })}
                      placeholder="Details about their chain..."
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* TAB 4: Search Criteria */}
            <TabsContent value="search" className="mt-0 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Valid Until</Label>
                  <Input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Price (from)</Label>
                  <Select value={formData.price_min} onValueChange={(v) => setFormData({ ...formData, price_min: v })}>
                    <SelectTrigger><SelectValue placeholder="No minimum" /></SelectTrigger>
                    <SelectContent>
                      {PRICE_OPTIONS.map(p => <SelectItem key={p.value || "none"} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Price (to)</Label>
                  <Select value={formData.price_max} onValueChange={(v) => setFormData({ ...formData, price_max: v })}>
                    <SelectTrigger><SelectValue placeholder="No maximum" /></SelectTrigger>
                    <SelectContent>
                      {PRICE_OPTIONS.map(p => <SelectItem key={p.value || "none-max"} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Bedrooms (from)</Label>
                  <Select value={formData.bedrooms_min} onValueChange={(v) => setFormData({ ...formData, bedrooms_min: v })}>
                    <SelectTrigger><SelectValue placeholder="Studio" /></SelectTrigger>
                    <SelectContent>
                      {BEDROOM_OPTIONS.map(b => <SelectItem key={b.value || "studio"} value={b.value}>{b.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Bedrooms (to)</Label>
                  <Select value={formData.bedrooms_max} onValueChange={(v) => setFormData({ ...formData, bedrooms_max: v })}>
                    <SelectTrigger><SelectValue placeholder="Maximum" /></SelectTrigger>
                    <SelectContent>
                      {BEDROOM_OPTIONS.map(b => <SelectItem key={b.value || "max"} value={b.value}>{b.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Property Types */}
              <Card>
                <CardHeader className="py-3 px-4 bg-slate-50">
                  <CardTitle className="text-sm font-medium">Property Types</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid grid-cols-3 gap-2">
                    {PROPERTY_TYPES.map(type => (
                      <label key={type} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={formData.property_types.includes(type)}
                          onCheckedChange={() => toggleArrayItem("property_types", type)}
                        />
                        {type}
                      </label>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              {/* Areas */}
              <Card>
                <CardHeader className="py-3 px-4 bg-slate-50">
                  <CardTitle className="text-sm font-medium">Areas of Interest</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid grid-cols-4 gap-2">
                    {AREAS.map(area => (
                      <label key={area} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={formData.areas.includes(area)}
                          onCheckedChange={() => toggleArrayItem("areas", area)}
                        />
                        {area}
                      </label>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              {/* Additional Notes */}
              <div>
                <Label>Additional Notes</Label>
                <Textarea
                  value={formData.additional_notes}
                  onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                  placeholder="e.g. 4 bed detached, Biddenham, Bromham, closer to Milton Keynes, family oriented, £500,000-£600,000"
                  rows={4}
                />
              </div>
            </TabsContent>
          </ScrollArea>
          
          {/* Footer Actions */}
          <div className="flex justify-between items-center p-6 pt-0 border-t mt-4">
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <div className="flex gap-2">
              {activeTab !== "basic" && (
                <Button variant="outline" onClick={() => {
                  const tabs = ["basic", "status", "finance", "search"];
                  const idx = tabs.indexOf(activeTab);
                  if (idx > 0) setActiveTab(tabs[idx - 1]);
                }}>
                  Previous
                </Button>
              )}
              {activeTab !== "search" ? (
                <Button onClick={() => {
                  const tabs = ["basic", "status", "finance", "search"];
                  const idx = tabs.indexOf(activeTab);
                  if (idx < tabs.length - 1) setActiveTab(tabs[idx + 1]);
                }}>
                  Next
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Save Applicant
                </Button>
              )}
            </div>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
